import { Injectable } from '@angular/core';
import { Parser } from '@json2csv/plainjs';
import { utils, WorkBook, WorkSheet, write } from 'xlsx';
import { DateUtil } from '../../shared/DateUtil';
import { ColumnSelection, FilterAs, TableRow } from './ColumnSelection';
import { TableReportProject } from './TableReportProject';

@Injectable({
  providedIn: 'root',
})
export class CSVService {
  private createJSON(
    projects: TableReportProject[],
    columnsSelection: ColumnSelection[],
    visibleRows: TableRow[],
    useRawNbrs: boolean
  ): any[] {
    const result = [];
    for (const row of visibleRows) {
      const jsonRow = {};
      const project: TableReportProject = projects.find(
        (p) => p.id === row.projectID
      );
      for (const columnSelection of columnsSelection) {
        let cellBody = row[columnSelection.template.id];
        if (useRawNbrs) {
          const value = columnSelection.template.getter(project);
          if (value || value === 0) {
            if (
              FilterAs[columnSelection.template.filterAsA] === FilterAs.date
            ) {
              cellBody = CSVService.handleDate(value) || '';
            } else if (
              FilterAs[columnSelection.template.filterAsA] === FilterAs.sri
            ) {
              cellBody = value;
            } else if (
              FilterAs[columnSelection.template.filterAsA] === FilterAs.number
            ) {
              cellBody = value;
            } else if (
              FilterAs[columnSelection.template.filterAsA] ===
              FilterAs.finNumber
            ) {
              if (value.fullAccess) {
                cellBody = value.value;
              } else {
                cellBody = 'Access Required';
              }
            } else if (
              FilterAs[columnSelection.template.filterAsA] === FilterAs.percent
            ) {
              cellBody = value + '%';
            }
          } else {
            cellBody = '';
          }
        } else {
          if (FilterAs[columnSelection.template.filterAsA] !== FilterAs.sri) {
            cellBody += String.fromCodePoint(9);
          }
        }
        jsonRow[columnSelection.template.id] = cellBody;
      }
      // console.log('createJSON.jsonRow=', jsonRow);
      result.push(jsonRow);
    }
    return result;
  }

  // tslint:disable-next-line:member-ordering
  private static handleDate(value): Date {
    if (typeof value === 'string') {
      return DateUtil.anyToDate(value);
    } else {
      return value;
    }
  }

  // tslint:disable-next-line:member-ordering
  private static buildCsvColumnDefinition(
    columnsSelection: ColumnSelection[]
  ): CSVColumn[] {
    const result: CSVColumn[] = [];
    for (const columnSelection of columnsSelection) {
      if (columnSelection.selected) {
        result.push(
          Object.assign(new CSVColumn(), {
            label: columnSelection.template.label,
            stringify: false,
            defaultValue: '',
            value: columnSelection.template.id,
            index: columnSelection.index,
          })
        );
      }
    }
    return result.sort((n1, n2) => n1.index - n2.index);
  }

  public createAndDownloadXLSX(
    projects: TableReportProject[],
    columnsSelection: ColumnSelection[],
    visibleRows: TableRow[],
    fileName: string
  ): void {
    const json: any[] = this.createJSON(
      projects,
      columnsSelection,
      visibleRows,
      true
    );
    json.unshift(this.getFirstLine(columnsSelection));
    this.createXlsxWorkBook(json, fileName);
  }

  private getFirstLine(columnsSelection: ColumnSelection[]) {
    const columnDefinition: CSVColumn[] =
      CSVService.buildCsvColumnDefinition(columnsSelection);
    const firstLine = {};
    columnDefinition.forEach((value) => {
      firstLine[value.value] = value.label;
    });
    return firstLine;
  }

  public createAndDownloadCsv(
    projects: TableReportProject[],
    columnsSelection: ColumnSelection[],
    visibleRows: TableRow[],
    fileName
  ): void {
    const csv: string = this.createCsv(projects, columnsSelection, visibleRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    this.downloadFilePrependDateToFileName(blob, fileName, 'csv');
  }

  private createCsv(
    projects: TableReportProject[],
    columnsSelection: ColumnSelection[],
    visibleRows: TableRow[]
  ): string {
    const data: any[] = this.createJSON(
      projects,
      columnsSelection,
      visibleRows,
      false
    );
    const columnDefinition: CSVColumn[] =
      CSVService.buildCsvColumnDefinition(columnsSelection);
    const json2csvParser = new Parser({
      fields: columnDefinition,
      delimiter: ';',
      withBOM: true, // to keep encoding
    });

    return json2csvParser.parse(data);
  }

  private prepareToDownload(
    columnDefinition: CSVColumn[],
    json: any[],
    fileName: string,
    extension: string
  ) {
    if (extension === 'csv') {
      const json2csvParser = new Parser({
        fields: columnDefinition,
        delimiter: ';',
        withBOM: true, // to keep encoding
      });
      const csv: string = json2csvParser.parse(json);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      this.downloadFilePrependDateToFileName(blob, fileName, extension);
    } else {
      const firstJSONLine = {};
      columnDefinition.forEach((value) => {
        firstJSONLine[value.value] = value.label;
      });
      json.unshift(firstJSONLine);
      this.createXlsxWorkBook(json, fileName);
    }
  }

  public createXlsxWorkBook(json: any[], fileName: string) {
    const workSheet: WorkSheet = utils.json_to_sheet(json, {
      skipHeader: true,
      cellDates: true,
    });
    const workBook: WorkBook = utils.book_new();
    utils.book_append_sheet(workBook, workSheet, 'DigiResSheet');
    const workBookData = write(workBook, {
      bookType: 'xlsx',
      bookSST: true,
      type: 'binary',
    });

    this.prepareDownloadOfXlsx(workBookData, fileName);
  }

  public prepareDownloadOfXlsx(workBookData: WorkBook, fileName: string) {
    function s2ab(s) {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i !== s.length; ++i) {
        // tslint:disable-next-line:no-bitwise
        view[i] = s.charCodeAt(i) & 0xff;
      }
      return buf;
    }
    this.downloadFilePrependDateToFileName(
      new Blob([s2ab(workBookData)], { type: 'application/octet-stream' }),
      fileName,
      'xlsx'
    );
  }

  // noinspection JSMethodCanBeStatic
  public downloadFilePrependDateToFileName(blob: Blob, fileName: string, extension: string): void {
    const timestamp = DateUtil.fileNameTimeStamp(new Date(Date.now()));
    const fullFileName = `${timestamp}_DigiRES_${fileName}`;
    this.downloadFile(blob, fullFileName, extension);
  }

  // noinspection JSMethodCanBeStatic
  public downloadFile(blob: Blob, fileName: string, extension: string): void {
    const linkEl = document.createElement('a');
    linkEl.href = URL.createObjectURL(blob);
    linkEl.setAttribute('download', fileName + '.' + extension);
    linkEl.target = '_self';
    document.body.appendChild(linkEl);
    linkEl.click();
  }
}
export class CSVColumn {
  label: string;
  stringify: boolean;
  defaultValue: string;
  value: string;
  index: number;
}
